import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { idVerificationAPI, IDVerificationTask, Attachment } from '../api/idVerification';
import { useAuth } from '../context/AuthContext';
import './IDVerificationTasks.css';

interface PaginatedTasks {
  tasks: IDVerificationTask[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
}

const ITEMS_PER_PAGE = 50;
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.heic', '.heif'];

// Rejection reason options with their full text
const REJECTION_REASONS = {
  blurry: {
    label: 'Image too blurry',
    getText: () => ' the image/photograph is too blurry to make out its contents accurately and/or reliably. Can you kindly please re-attach the document so that it is clear, in focus and is sufficiently big enough in size to view.'
  },
  wrong_format: {
    label: 'Image received in wrong format',
    getText: () => ' we are unable to view the document in the format it has been attached. Can you kindly please re-attach the document in a format we can access i.e. a JPEG or IMG file.'
  },
  wrong_person: {
    label: 'Image received of wrong person',
    getText: (clientName: string) => ` the ID relates to a different person to that on this account. Can you kindly please re-attach the ID document for ${clientName} only.`
  },
  nothing_attached: {
    label: 'Nothing attached',
    getText: () => ' there was no attachment within the email. Can you kindly please re-attach the ID in a format we can access i.e. a JPEG or IMG file.'
  },
  other: {
    label: 'Other (custom reason)',
    getText: () => ''
  }
} as const;

const isImageAttachment = (attachment: Attachment) => {
  const type = (attachment.contentType || '').toLowerCase();
  if (type.startsWith('image/')) {
    return true;
  }
  const filename = (attachment.filename || '').toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => filename.endsWith(ext));
};

const isPdfAttachment = (attachment: Attachment) => {
  const type = (attachment.contentType || '').toLowerCase();
  if (type === 'application/pdf') {
    return true;
  }
  const filename = (attachment.filename || '').toLowerCase();
  return filename.endsWith('.pdf');
};

const getAttachmentPreviewUrl = (attachment: Attachment) => {
  return (
    attachment.previewUrl ||
    attachment.thumbnailUrl ||
    attachment.url ||
    attachment.secureUrl ||
    ''
  );
};

export const IDVerificationTasks: React.FC = () => {
  const { isAdmin } = useAuth();
  const [pendingTasks, setPendingTasks] = useState<PaginatedTasks>({
    tasks: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 1
  });
  const [completedTasks, setCompletedTasks] = useState<PaginatedTasks>({
    tasks: [],
    totalCount: 0,
    currentPage: 1,
    totalPages: 1
  });
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<IDVerificationTask | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionType, setRejectionType] = useState<string>('');
  const [customRejectionReason, setCustomRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [tabValue, setTabValue] = useState<'pending' | 'completed'>('pending');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'asc' | 'desc' }>({ 
    field: 'createdAt', 
    direction: 'desc' 
  });
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [pageInput, setPageInput] = useState<string>('');

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch tasks for the active tab only (single query per page flip)
  const fetchTasks = useCallback(async (page: number = 1) => {
    setLoading(true);
    setIsSearching(!!debouncedSearchTerm);

    try {
      const response = await idVerificationAPI.listTasks({
        status: tabValue === 'pending' ? 'todo' : 'completed',
        search: debouncedSearchTerm || undefined,
        skip: (page - 1) * ITEMS_PER_PAGE,
        limit: ITEMS_PER_PAGE,
        sortBy: sortConfig.field,
        sortOrder: sortConfig.direction
      });

      const paginated: PaginatedTasks = {
        tasks: response.tasks,
        totalCount: response.total,
        currentPage: page,
        totalPages: Math.max(1, Math.ceil(response.total / ITEMS_PER_PAGE))
      };

      if (tabValue === 'pending') {
        setPendingTasks(paginated);
      } else {
        setCompletedTasks(paginated);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setMessage({ type: 'error', text: 'Failed to fetch tasks' });
    } finally {
      setLoading(false);
      setIsSearching(false);
    }
  }, [debouncedSearchTerm, sortConfig, tabValue]);

  // Initial load and refresh on filter changes. fetchTasks changes identity
  // when the active tab / search / sort changes, so this fetches page 1 of the
  // newly-active view each time.
  useEffect(() => {
    fetchTasks(1);
  }, [fetchTasks]);

  // Switching tabs always shows page 1 of the newly-active tab. Reset the
  // stored currentPage up-front (the effect above also fetches page 1 on the
  // tabValue change) so the paginator can never briefly display a stale page
  // number from a previous visit, and clear the page-jump input. Per-tab page
  // position is intentionally NOT preserved across switches.
  const handleTabChange = (tab: 'pending' | 'completed') => {
    if (tab === tabValue) return;
    setPageInput('');
    if (tab === 'pending') {
      setPendingTasks(prev => ({ ...prev, currentPage: 1 }));
    } else {
      setCompletedTasks(prev => ({ ...prev, currentPage: 1 }));
    }
    setTabValue(tab);
  };

  // Handle task approval
  const handleApprove = async (task: IDVerificationTask) => {
    if (!window.confirm(`Are you sure you want to approve ID verification for ${task.clientName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      await idVerificationAPI.approveTask(task.id);
      setMessage({ type: 'success', text: `ID verification approved for ${task.clientName}` });
      fetchTasks(tabValue === 'pending' ? pendingTasks.currentPage : completedTasks.currentPage);
      setViewDialogOpen(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Error approving task:', error);
      setMessage({ type: 'error', text: 'Failed to approve ID verification' });
    } finally {
      setProcessing(false);
    }
  };

  // Get the full rejection reason text based on selected type
  const getFullRejectionReason = useCallback(() => {
    if (!rejectionType || !selectedTask) return '';

    if (rejectionType === 'other') {
      return customRejectionReason;
    }

    const reasonConfig = REJECTION_REASONS[rejectionType as keyof typeof REJECTION_REASONS];
    if (!reasonConfig) return '';

    if (rejectionType === 'wrong_person') {
      return (reasonConfig.getText as (name: string) => string)(selectedTask.clientName || 'the client');
    }

    return (reasonConfig.getText as () => string)();
  }, [rejectionType, customRejectionReason, selectedTask]);

  // Handle task rejection
  const handleReject = async () => {
    const fullReason = getFullRejectionReason();

    if (!selectedTask || !rejectionType) {
      setMessage({ type: 'error', text: 'Please select a rejection reason' });
      return;
    }

    if (rejectionType === 'other' && !customRejectionReason.trim()) {
      setMessage({ type: 'error', text: 'Please provide a custom rejection reason' });
      return;
    }

    setProcessing(true);
    try {
      await idVerificationAPI.rejectTask(selectedTask.id, fullReason);
      setMessage({ type: 'success', text: `ID verification rejected for ${selectedTask.clientName}` });
      fetchTasks(tabValue === 'pending' ? pendingTasks.currentPage : completedTasks.currentPage);
      setRejectDialogOpen(false);
      setViewDialogOpen(false);
      setSelectedTask(null);
      setRejectionReason('');
      setRejectionType('');
      setCustomRejectionReason('');
    } catch (error) {
      console.error('Error rejecting task:', error);
      setMessage({ type: 'error', text: 'Failed to reject ID verification' });
    } finally {
      setProcessing(false);
    }
  };

  // Handle mark as duplicate
  const handleMarkAsDuplicate = async (task: IDVerificationTask) => {
    if (!window.confirm(`Are you sure you want to mark this as a duplicate submission?`)) {
      return;
    }

    setProcessing(true);
    try {
      await idVerificationAPI.markAsDuplicate(task.id);
      setMessage({ type: 'success', text: 'Task marked as duplicate' });
      fetchTasks(tabValue === 'pending' ? pendingTasks.currentPage : completedTasks.currentPage);
      setViewDialogOpen(false);
      setSelectedTask(null);
    } catch (error) {
      console.error('Error marking as duplicate:', error);
      setMessage({ type: 'error', text: 'Failed to mark as duplicate' });
    } finally {
      setProcessing(false);
    }
  };

  // Toggle task expansion
  const toggleTaskExpansion = (taskId: string) => {
    setExpandedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  // Handle sorting
  const handleSort = (field: string) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Format date for display (UK format)
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge class
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'badge-warning';
      case 'in_progress': return 'badge-info';
      case 'approved': return 'badge-success';
      case 'rejected': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  // Render task row
  const renderTaskRow = (task: IDVerificationTask) => {
    const isExpanded = expandedTasks.has(task.id);
    
    return (
      <React.Fragment key={task.id}>
        <tr className="task-row" onClick={() => toggleTaskExpansion(task.id)}>
          <td className="task-expand">
            <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▶</span>
          </td>
          <td className="task-client">
            <div className="client-info">
              <strong>{task.clientName || 'Unknown'}</strong>
              <br />
              <small className="text-muted">{task.clientEmail || 'No email'}</small>
            </div>
          </td>
          <td className="task-attachments">
            {task.hasPhotos && <span className="badge badge-info">📷 {task.attachmentCount || 0}</span>}
          </td>
          <td className="task-status">
            <span className={`badge ${getStatusBadgeClass(task.status)}`}>
              {task.status.replace('_', ' ').toUpperCase()}
            </span>
          </td>
          <td className="task-date">{formatDate(task.createdAt)}</td>
          <td className="task-actions" onClick={(e) => e.stopPropagation()}>
            <button
              className="btn btn-sm btn-primary"
              onClick={() => handleView(task)}
            >
              View
            </button>
            {task.status === 'pending' && (
              <>
                <button
                  className="btn btn-sm btn-success ml-2"
                  onClick={() => handleApprove(task)}
                  disabled={processing}
                >
                  Approve
                </button>
                <button
                  className="btn btn-sm btn-danger ml-2"
                  onClick={() => {
                    setSelectedTask(task);
                    setRejectDialogOpen(true);
                  }}
                  disabled={processing}
                >
                  Reject
                </button>
              </>
            )}
          </td>
        </tr>
        {isExpanded && (
          <tr className="task-details-row">
            <td colSpan={6}>
              <div className="task-details">
                <div className="detail-section">
                  <h5>Task Details</h5>
                  <p><strong>ID:</strong> {task.id}</p>
                  <p><strong>Client ID:</strong> {task.clientId}</p>
                  <p><strong>Tracking ID:</strong> {task.trackingId || 'N/A'}</p>
                  <p><strong>From:</strong> {task.from || 'N/A'}</p>
                  <p><strong>Created:</strong> {formatDate(task.createdAt)}</p>
                  {task.processedAt && (
                    <>
                      <p><strong>Processed:</strong> {formatDate(task.processedAt)}</p>
                      <p><strong>Processed By:</strong> {task.processedBy || 'N/A'}</p>
                    </>
                  )}
                </div>
                
                {task.aiProcessed && (
                  <div className="detail-section">
                    <h5>AI Analysis</h5>
                    <p><strong>Confidence:</strong> {(task.aiConfidence || 0) * 100}%</p>
                    {task.aiNotes && <p><strong>Notes:</strong> {task.aiNotes}</p>}
                  </div>
                )}
                
                {task.rejectionReason && (
                  <div className="detail-section">
                    <h5>Rejection Details</h5>
                    <p><strong>Reason:</strong> {task.rejectionReason}</p>
                    <p><strong>Email Sent:</strong> {task.rejectionEmailSent ? 'Yes' : 'No'}</p>
                  </div>
                )}
                
                {task.bodyPreview && (
                  <div className="detail-section">
                    <h5>Email Preview</h5>
                    <p>{task.bodyPreview}</p>
                  </div>
                )}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  // Get current tasks based on tab
  const currentTasks = tabValue === 'pending' ? pendingTasks : completedTasks;

  // Email-tasks style pagination bar (First/Previous/numbers/Next/Last/Go to)
  const renderPagination = () => {
    if (currentTasks.totalPages <= 1) return null;

    const maxPagesToShow = 7;
    let startPage = Math.max(1, currentTasks.currentPage - 3);
    let endPage = Math.min(currentTasks.totalPages, startPage + maxPagesToShow - 1);
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    const goToPage = () => {
      const pageNum = parseInt(pageInput, 10);
      if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= currentTasks.totalPages) {
        fetchTasks(pageNum);
        setPageInput('');
      }
    };

    return (
      <div className="pagination">
        {loading && (
          <span style={{ marginRight: '10px', color: '#01B8C0' }}>Loading...</span>
        )}
        <button
          className="pagination-btn"
          onClick={() => fetchTasks(1)}
          disabled={loading || currentTasks.currentPage === 1}
        >
          First
        </button>
        <button
          className="pagination-btn"
          onClick={() => fetchTasks(currentTasks.currentPage - 1)}
          disabled={loading || currentTasks.currentPage === 1}
        >
          Previous
        </button>

        {startPage > 1 && (
          <>
            <button className="pagination-btn" onClick={() => fetchTasks(1)} disabled={loading}>1</button>
            {startPage > 2 && <span className="pagination-ellipsis">...</span>}
          </>
        )}

        {Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i).map(page => (
          <button
            key={page}
            className={`pagination-btn ${page === currentTasks.currentPage ? 'active' : ''}`}
            onClick={() => fetchTasks(page)}
            disabled={loading}
          >
            {page}
          </button>
        ))}

        {endPage < currentTasks.totalPages && (
          <>
            {endPage < currentTasks.totalPages - 1 && <span className="pagination-ellipsis">...</span>}
            <button
              className="pagination-btn"
              onClick={() => fetchTasks(currentTasks.totalPages)}
              disabled={loading}
            >
              {currentTasks.totalPages}
            </button>
          </>
        )}

        <button
          className="pagination-btn"
          onClick={() => fetchTasks(currentTasks.currentPage + 1)}
          disabled={loading || currentTasks.currentPage === currentTasks.totalPages}
        >
          Next
        </button>
        <button
          className="pagination-btn"
          onClick={() => fetchTasks(currentTasks.totalPages)}
          disabled={loading || currentTasks.currentPage === currentTasks.totalPages}
        >
          Last
        </button>

        <div className="page-jump" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '12px', gap: '6px' }}>
          <span style={{ fontSize: '14px', color: '#6b7280' }}>Go to:</span>
          <input
            type="number"
            min={1}
            max={currentTasks.totalPages}
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') goToPage(); }}
            placeholder={String(currentTasks.currentPage)}
            style={{
              width: '60px',
              padding: '4px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              fontSize: '14px',
              textAlign: 'center'
            }}
            disabled={loading}
          />
          <button
            type="button"
            className="pagination-btn"
            onClick={goToPage}
            disabled={loading}
          >
            Go
          </button>
        </div>

        <span className="pagination-info">
          Page {currentTasks.currentPage} of {currentTasks.totalPages} ({currentTasks.totalCount} total)
        </span>
      </div>
    );
  };

  // Fetch single task with enriched attachments (SAS URLs) before opening modal
  const handleView = async (task: IDVerificationTask) => {
    try {
      setProcessing(true);
      const full = await idVerificationAPI.getTask(task.id);
      setSelectedTask(full);
    } catch (e) {
      console.error('Error loading full task, falling back to list item:', e);
      setSelectedTask(task);
    } finally {
      setViewDialogOpen(true);
      setProcessing(false);
    }
  };

  return (
    <div className="id-verification-tasks">
      <div className="tasks-header">
        <h2>ID Verification Tasks</h2>
        <div className="header-actions">
          <input
            type="text"
            className="form-control search-input"
            placeholder="Search by client name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
          <button className="close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      <div className="tasks-tabs">
        <button
          className={`tab-button ${tabValue === 'pending' ? 'active' : ''}`}
          onClick={() => handleTabChange('pending')}
        >
          To-Do ({pendingTasks.totalCount})
        </button>
        <button
          className={`tab-button ${tabValue === 'completed' ? 'active' : ''}`}
          onClick={() => handleTabChange('completed')}
        >
          Completed ({completedTasks.totalCount})
        </button>
      </div>

      <div className="tasks-content">
        {loading && !isSearching ? (
          <div className="loading">Loading tasks...</div>
        ) : currentTasks.tasks.length === 0 ? (
          <div className="no-tasks">
            {isSearching ? 'No tasks found matching your search.' : 'No tasks to display.'}
          </div>
        ) : (
          <>
            {renderPagination()}

            <table className="tasks-table">
              <thead>
                <tr>
                  <th></th>
                  <th onClick={() => handleSort('clientName')} className="sortable">
                    Client {sortConfig.field === 'clientName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Attachments</th>
                  <th onClick={() => handleSort('status')} className="sortable">
                    Status {sortConfig.field === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('createdAt')} className="sortable">
                    Created {sortConfig.field === 'createdAt' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentTasks.tasks.map(renderTaskRow)}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* View Dialog */}
      {viewDialogOpen && selectedTask && (
        <div className="modal-overlay" onClick={() => setViewDialogOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>ID Verification Details</h3>
              <button className="close-button" onClick={() => setViewDialogOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="task-detail-grid">
                <div className="detail-row">
                  <label>Client Name:</label>
                  <span>{selectedTask.clientName || 'Unknown'}</span>
                </div>
                <div className="detail-row">
                  <label>Client Email:</label>
                  <span>{selectedTask.clientEmail || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Client DOB:</label>
                  <span>{selectedTask.clientDOB ? new Date(selectedTask.clientDOB).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <label>Status:</label>
                  <span className={`badge ${getStatusBadgeClass(selectedTask.status)}`}>
                    {selectedTask.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>
                <div className="detail-row">
                  <label>Attachments:</label>
                  <span>{selectedTask.attachmentCount || 0} file(s)</span>
                </div>
                <div className="detail-row">
                  <label>Created:</label>
                  <span>{formatDate(selectedTask.createdAt)}</span>
                </div>
                {selectedTask.processedAt && (
                  <div className="detail-row">
                    <label>Processed:</label>
                    <span>{formatDate(selectedTask.processedAt)} by {selectedTask.processedBy}</span>
                  </div>
                )}
                {selectedTask.rejectionReason && (
                  <div className="detail-row full-width">
                    <label>Rejection Reason:</label>
                    <span>{selectedTask.rejectionReason}</span>
                  </div>
                )}
              </div>
              
              {selectedTask.attachments && selectedTask.attachments.length > 0 && (
                <div className="attachments-section">
                  <h4>Attached Documents</h4>
                  <div className="attachments-grid">
                    {selectedTask.attachments.map((att, index) => {
                      const previewUrl = getAttachmentPreviewUrl(att);
                      const fileUrl = att.url || att.secureUrl || previewUrl;
                      const isImage = isImageAttachment(att);
                      const isPdf = isPdfAttachment(att);

                      return (
                        <div key={index} className="attachment-item">
                          {isImage && previewUrl ? (
                            <div className="attachment-image-wrapper">
                              <img
                                src={previewUrl}
                                alt={att.filename || `attachment-${index + 1}`}
                                loading="lazy"
                              />
                            </div>
                          ) : isPdf && fileUrl ? (
                            <div className="attachment-pdf-wrapper">
                              <iframe
                                src={fileUrl}
                                title={att.filename || `PDF-${index + 1}`}
                                className="pdf-preview"
                              />
                            </div>
                          ) : (
                            <div className="file-icon">📄</div>
                          )}
                          <p>{att.filename || 'Attachment'}</p>
                          {fileUrl ? (
                            <div className="attachment-actions">
                              {isImage && (
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="attachment-link"
                                >
                                  View Full Size
                                </a>
                              )}
                              {isPdf && (
                                <a
                                  href={fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="attachment-link"
                                >
                                  Open PDF
                                </a>
                              )}
                              <a
                                href={fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm"
                                download
                              >
                                Download
                              </a>
                            </div>
                          ) : (
                            <span className="text-muted attachment-note">File unavailable</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {selectedTask.status === 'pending' && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={() => handleApprove(selectedTask)}
                    disabled={processing}
                  >
                    Approve
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => setRejectDialogOpen(true)}
                    disabled={processing}
                  >
                    Reject
                  </button>
                  <button
                    className="btn btn-warning"
                    onClick={() => handleMarkAsDuplicate(selectedTask)}
                    disabled={processing}
                  >
                    Mark as Duplicate
                  </button>
                </>
              )}
              <button className="btn btn-secondary" onClick={() => setViewDialogOpen(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {rejectDialogOpen && selectedTask && (
        <div className="modal-overlay" onClick={() => setRejectDialogOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject ID Verification</h3>
              <button className="close-button" onClick={() => setRejectDialogOpen(false)}>×</button>
            </div>
            <div className="modal-body reject-modal-body">
              <div className="reject-intro">
                <p>You are about to reject the ID verification for:</p>
                <p className="client-name-highlight">{selectedTask.clientName}</p>
              </div>

              <div className="form-group">
                <label htmlFor="rejection-type">Select Rejection Reason</label>
                <select
                  id="rejection-type"
                  className="form-control"
                  value={rejectionType}
                  onChange={(e) => {
                    setRejectionType(e.target.value);
                    setCustomRejectionReason('');
                  }}
                >
                  <option value="">-- Select a reason --</option>
                  {Object.entries(REJECTION_REASONS).map(([key, value]) => (
                    <option key={key} value={key}>{value.label}</option>
                  ))}
                </select>
              </div>

              {rejectionType === 'other' && (
                <div className="form-group">
                  <label htmlFor="custom-reason">Custom Reason</label>
                  <textarea
                    id="custom-reason"
                    className="form-control"
                    rows={4}
                    value={customRejectionReason}
                    onChange={(e) => setCustomRejectionReason(e.target.value)}
                    placeholder="Please provide a custom reason for rejection..."
                  />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-danger"
                onClick={handleReject}
                disabled={!rejectionType || (rejectionType === 'other' && !customRejectionReason.trim()) || processing}
              >
                {processing ? 'Sending...' : 'Confirm Rejection & Send Email'}
              </button>
              <button className="btn btn-secondary" onClick={() => setRejectDialogOpen(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
